// Support tickets for the hosted dashboard.
//
// Assumed contract (PRD US-003 / FR-3 / FR-4), relative to axiosInstance's
// /api/v1/ base URL:
//
//   POST support/tickets
//   body: { message: string, loom_url?: string }
//   201:  { id, ticket_number, created_at }
//   400:  { msg, code: 'VALIDATION_ERROR', field }
//   401:  { msg }
//   429:  rate limit
//   500:  { error }
//
// The customer only sends the two fields they can edit. Name, email, sites,
// and billing are filled in on the server from the authenticated user.
// Do not add those fields here, even if a caller passes them in.

import axiosInstance from './axiosConfig';
import { apiStatus, authHeaders, parseApiError } from './billingApi';

export const SUPPORT_TICKETS_PATH = 'support/tickets';

export const MESSAGE_MAX_LENGTH = 5000;

export const RATE_LIMIT_MESSAGE =
  "You've sent several requests recently. Please wait a few minutes and try again.";

const EMPTY_MESSAGE = 'Please enter a message.';
const LONG_MESSAGE = 'Message must be 5,000 characters or fewer.';
const LOOM_MESSAGE = 'Enter a Loom share link, like https://www.loom.com/share/abc123.';

// Host is case-insensitive. The /share/<id> path stays case-sensitive, matching
// ^https://(www\.)?loom\.com/share/[A-Za-z0-9]+(\?[^\s]*)?$
export function isLoomShareUrl(value) {
  const match = /^(https:\/\/)([^/?#]+)(\/share\/[A-Za-z0-9]+(\?[^\s]*)?)$/.exec(value);
  if (!match) return false;
  const host = match[2].toLowerCase();
  return host === 'loom.com' || host === 'www.loom.com';
}

// Returns the request body when the form is valid, or field errors when it isn't.
// An empty Loom field is left off the body (the server stores that as NULL).
export function validateSupportTicket({ message, loomUrl }) {
  const trimmedMessage = typeof message === 'string' ? message.trim() : '';
  const trimmedLoom = typeof loomUrl === 'string' ? loomUrl.trim() : '';
  const errors = {};

  if (!trimmedMessage) {
    errors.message = EMPTY_MESSAGE;
  } else if (trimmedMessage.length > MESSAGE_MAX_LENGTH) {
    errors.message = LONG_MESSAGE;
  }

  if (trimmedLoom && !isLoomShareUrl(trimmedLoom)) {
    errors.loomUrl = LOOM_MESSAGE;
  }

  const body = { message: trimmedMessage };
  if (trimmedLoom) body.loom_url = trimmedLoom;

  return {
    ok: Object.keys(errors).length === 0,
    errors,
    body,
  };
}

export async function createSupportTicket({ message, loom_url } = {}) {
  const body = { message };
  if (typeof loom_url === 'string' && loom_url.length > 0) {
    body.loom_url = loom_url;
  }

  const { data } = await axiosInstance.post(SUPPORT_TICKETS_PATH, body, authHeaders());
  return data;
}

export function supportErrorMessage(requestError) {
  if (apiStatus(requestError) === 429) return RATE_LIMIT_MESSAGE;
  return parseApiError(requestError);
}
